import { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Icon, type IconName } from '../ui/icons';

export const cx = (...xs: (string | undefined | null | false)[]): string => 
  xs.filter(Boolean).join(' ');

// Status tokens from Figma palette
export const TONE = {
  ok: { bg: '#C6F3D8', fg: '#159D4C', ring: '#159D4C' },
  warn: { bg: '#FDF9D3', fg: '#CA9401', ring: '#F9C12E' },
  crit: { bg: '#FDD3D3', fg: '#F40B0B', ring: '#F40B0B' },
  info: { bg: '#DDF4FF', fg: '#0A69DA', ring: '#0A69DA' },
  muted: { bg: '#EEEFF2', fg: '#6B7280', ring: '#9CA3AF' },
  draft: { bg: '#EDE5FC', fg: '#7F56D9', ring: '#7F56D9' },
} as const;

export type ToneType = keyof typeof TONE;

interface BadgeProps {
  tone?: ToneType;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}

export function Badge({ tone = 'muted', children, dot = false, className }: BadgeProps) {
  const t = TONE[tone];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-[4px] px-2 py-[3px]',
        'font-display text-[12px] font-semibold whitespace-nowrap',
        className
      )}
      style={{ background: t.bg, color: t.fg }}
    >
      {dot && (
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: t.ring }} />
      )}
      {children}
    </span>
  );
}

interface StatusDotProps {
  tone?: ToneType;
  size?: number;
}

export function StatusDot({ tone = 'ok', size = 8 }: StatusDotProps) {
  const t = TONE[tone];
  return (
    <span
      className="inline-block rounded-full shrink-0"
      style={{ width: size, height: size, background: t.ring }}
    />
  );
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}

export function PageHeader({ title, subtitle, right }: PageHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0 flex-1">
        <h1 className="font-display font-bold text-ink leading-none text-[32px]">{title}</h1>
        {subtitle && (
          <p className="font-display text-[15px] text-ink/75 max-w-[820px] mt-3">{subtitle}</p>
        )}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

interface StatProps {
  label: string;
  value: string | number;
  tone?: ToneType;
}

export function Stat({ label, value, tone = 'muted' }: StatProps) {
  const t = TONE[tone];
  return (
    <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
      <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">{label}</div>
      <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: t.fg }}>{value}</div>
    </div>
  );
}

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  navigate: (route: string) => void;
}

export function Breadcrumb({ items, navigate }: BreadcrumbProps) {
  return (
    <nav className="flex items-center gap-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          {i > 0 && <Icon name="chevron-right" size={12} className="text-ink/40" />}
          {item.to ? (
            <button
              onClick={() => navigate(item.to!)}
              className="font-display text-[14px] text-primary-500 hover:text-primary-600"
            >
              {item.label}
            </button>
          ) : (
            <span className="font-display text-[14px] text-ink/60">{item.label}</span>
          )}
        </div>
      ))}
    </nav>
  );
}

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fill?: boolean;
}

export function Sparkline({ data, width = 140, height = 36, color = '#FA500E', fill = true }: SparklineProps) {
  if (!data || !data.length) return null;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => [i * step, height - ((v - min) / range) * (height - 4) - 2]);
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' ');
  const area = `${d} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      {fill && (
        <path d={area} fill={color} opacity="0.10" />
      )}
      <path d={d} stroke={color} strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill={color} />
    </svg>
  );
}